import React, { useState, useEffect } from "react";
import { Card, Tabs, Table, Switch, TimePicker, Button, message, Typography, Select, Input, Space, Tag, Popconfirm, Modal, Form, InputNumber, Tooltip, Checkbox } from "antd";
import Alert from "../components/GxAlert";
import { DeleteOutlined, PlusOutlined, UserAddOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import BackupTab from "./settings/BackupTab";
import UpdateTab from "./settings/UpdateTab";
import RestoreTab from "./settings/RestoreTab";
import ProductionOptionsTab from "./settings/ProductionOptionsTab";
import {
  getWorkSchedule, updateWorkDay, getPayrollSettings, updatePayrollSettings,
  getUsers, getRoles, getPermissionCatalog, createUser, updateUser, deleteUser, changeMyPassword,
  getAuditLogs, getAuditOptions,
  getHolidays, createHoliday, deleteHoliday,
  getLeaves, createLeave, deleteLeave, getAdjacentHolidays,
  getEmployees, getAccountingSettings, updateAccountingSettings,
} from "../api/client";
import { JalaliDateInput, todayISO, currentJalali, JALALI_MONTHS, JALALI_YEARS, formatDurationFa, toJalali } from "../utils/dateUtils";
import { useThemeMode, PALETTE_OPTIONS, DENSITY_OPTIONS } from "../theme/ThemeContext";
import { useAuth } from "../context/AuthContext";
import { resetAllLayouts } from "../utils/layoutStore";
import { Segmented } from "antd";
import GxTable from "../components/GxTable";
import GxTabs from "../components/GxTabs";
import { filterByChildren } from "../utils/selectFilter";
import { faNum } from "../utils/faNumberFormat";
import { HOME_PAGES, homeFor, pagesFor } from "../utils/homePages";

const { Title, Text } = Typography;
const { Option } = Select;

// ==================== تب ظاهر ====================
function AppearanceTab() {
  const { mode, toggle, palette, setPalette, density, setDensity } = useThemeMode();
  return (
    <Card style={{ maxWidth: 640 }}>
      <Alert type="info" showIcon style={{ marginBottom: 18 }}
        message="ظاهر برنامه را شخصی‌سازی کنید. تغییرات بلافاصله اعمال و ذخیره می‌شوند." />

      <div style={{ marginBottom: 22 }}>
        <Text strong style={{ display: "block", marginBottom: 10 }}>پالت رنگ برند</Text>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
          {PALETTE_OPTIONS.map((p) => (
            <button key={p.value} onClick={() => setPalette(p.value)}
              style={{
                display: "flex", alignItems: "center", gap: 8, cursor: "pointer",
                padding: "8px 12px", borderRadius: 11, background: "var(--bg-surface)",
                border: `2px solid ${palette === p.value ? "var(--brand-fg)" : "var(--border)"}`,
              }}>
              <span style={{ display: "flex", borderRadius: 6, overflow: "hidden" }}>
                <span style={{ width: 16, height: 16, background: p.swatch[0] }} />
                <span style={{ width: 16, height: 16, background: p.swatch[1] }} />
              </span>
              <span style={{ fontSize: 13, fontWeight: 600 }}>{p.label}</span>
            </button>
          ))}
        </div>
      </div>

      <div style={{ marginBottom: 22 }}>
        <Text strong style={{ display: "block", marginBottom: 10 }}>حالت نمایش</Text>
        <Segmented value={mode} onChange={(v) => { if (v !== mode) toggle(); }}
          options={[{ value: "light", label: "روشن" }, { value: "dark", label: "تیره" }]} />
      </div>

      <div>
        <Text strong style={{ display: "block", marginBottom: 10 }}>تراکم فاصله‌گذاری</Text>
        <Segmented value={density} onChange={setDensity}
          options={DENSITY_OPTIONS.map((d) => ({ value: d.value, label: d.label }))} />
      </div>

      <div style={{ marginTop: 24, paddingTop: 18, borderTop: "1px solid var(--border)" }}>
        <Text strong style={{ display: "block", marginBottom: 6 }}>چیدمانِ ستون‌ها و تب‌ها</Text>
        <Text type="secondary" style={{ display: "block", marginBottom: 12, fontSize: 13 }}>
          ترتیبی که ستون‌های جدول‌ها و تب‌ها را با کشیدن جابه‌جا می‌کنید، برای حسابِ کاربریِ شما ذخیره می‌شود.
          برای بازگشت به حالتِ پیش‌فرض از دکمهٔ زیر استفاده کنید.
        </Text>
        <Popconfirm
          title="چیدمانِ همهٔ جدول‌ها و تب‌ها به حالتِ پیش‌فرض برگردد؟"
          okText="بله، بازنشانی کن" cancelText="انصراف"
          onConfirm={() => {
            const n = resetAllLayouts();
            message.success(`چیدمان بازنشانی شد${n ? ` (${faNum(n)} مورد)` : ""}. صفحه تازه می‌شود…`);
            setTimeout(() => window.location.reload(), 800);
          }}
        >
          <Button danger>بازنشانیِ چیدمانِ جدول‌ها و تب‌ها</Button>
        </Popconfirm>
      </div>
    </Card>
  );
}

const ROLE_LABELS = {
  admin: "مدیر", finance: "مالی", production: "تولید",
  attendance: "حضورغیاب", viewer: "مشاهده‌گر",
};

// ==================== تب ساعت کاری ====================
function WorkScheduleTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = () => {
    setLoading(true);
    getWorkSchedule().then(r => setRows(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const save = async (weekday, patch) => {
    try {
      await updateWorkDay(weekday, patch);
      message.success("ذخیره شد");
      load();
    } catch { message.error("خطا در ذخیره"); }
  };

  const columns = [
    { title: "روز", dataIndex: "weekday_name", width: 100 },
    { title: "روز کاری", dataIndex: "is_working", width: 90,
      render: (v, r) => <Switch checked={v} onChange={c => save(r.weekday, { is_working: c })} /> },
    { title: "باحقوق (تعطیلِ پرداختی)", dataIndex: "is_paid", width: 130,
      render: (v, r) => <Switch checked={v} onChange={c => save(r.weekday, { is_paid: c })} /> },
    { title: "شروع", dataIndex: "start_time", width: 110,
      render: (v, r) => (
        <TimePicker format="HH:mm" allowClear={false} disabled={!r.is_working}
          value={v ? dayjs(v, "HH:mm:ss") : null}
          onChange={t => save(r.weekday, { start_time: t.format("HH:mm:ss") })} />
      ) },
    { title: "پایان", dataIndex: "end_time", width: 110,
      render: (v, r) => (
        <TimePicker format="HH:mm" allowClear={false} disabled={!r.is_working}
          value={v ? dayjs(v, "HH:mm:ss") : null}
          onChange={t => save(r.weekday, { end_time: t.format("HH:mm:ss") })} />
      ) },
    { title: "دقیقهٔ مورد انتظار", dataIndex: "expected_minutes", width: 120,
      render: v => <Tag color="blue">{v}</Tag> },
    { title: "یک روز کامل حساب شود", dataIndex: "counts_as_full_day", width: 150,
      render: (v, r) => <Switch checked={v} onChange={c => save(r.weekday, { counts_as_full_day: c })} /> },
  ];

  return (
    <Card>
      <Alert type="info" showIcon style={{ marginBottom: 12 }}
        message="پنجرهٔ حضورِ هر روز مبنای محاسبهٔ کسرکار است. «یک روز کامل» برای روزهای کوتاه مثل پنجشنبه به‌کار می‌رود تا حضورِ کامل = ۱ روز حساب شود." />
      <GxTable dataSource={rows} columns={columns} rowKey="weekday" size="small"
        loading={loading} pagination={false} scroll={{ x: "max-content" }} />
    </Card>
  );
}

// ==================== تب تقویم تعطیلات ====================
function HolidaysTab() {
  const cj = currentJalali();
  const [rows, setRows] = useState([]);
  const [year, setYear] = useState(cj.year);
  const [date, setDate] = useState(todayISO());
  const [kind, setKind] = useState("paid_off");
  const [desc, setDesc] = useState("");

  const load = () => getHolidays({ year }).then(r => setRows(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year]);

  const add = async () => {
    try {
      await createHoliday({ holiday_date: date, kind, description: desc || null });
      message.success("تعطیلی ثبت شد");
      setDesc("");
      load();
    } catch { message.error("خطا"); }
  };

  const columns = [
    { title: "تاریخ", dataIndex: "jalali_date", width: 130 },
    { title: "نوع", dataIndex: "kind", width: 160,
      render: v => v === "paid_off"
        ? <Tag color="green">تعطیلِ باحقوق</Tag>
        : <Tag color="orange">باز / روز عادی</Tag> },
    { title: "توضیح", dataIndex: "description" },
    { title: "", width: 60,
      render: (_, r) => (
        <Popconfirm title="حذف شود؟" onConfirm={async () => { await deleteHoliday(r.id); load(); }}
          okText="بله" cancelText="خیر">
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ) },
  ];

  return (
    <Card
      title={<Space><Text>سال:</Text>
        <Select value={year} onChange={setYear} style={{ width: 90 }}>
          {JALALI_YEARS.map(y => <Option key={y} value={y}>{y}</Option>)}
        </Select></Space>}
    >
      <Space wrap style={{ marginBottom: 14 }}>
        <JalaliDateInput value={date} onChange={setDate} />
        <Select value={kind} onChange={setKind} style={{ width: 160 }}>
          <Option value="paid_off">تعطیلِ باحقوق</Option>
          <Option value="working">باز / روز عادی</Option>
        </Select>
        <Input placeholder="توضیح (اختیاری)" value={desc} onChange={e => setDesc(e.target.value)} style={{ width: 200 }} />
        <Button type="primary" icon={<PlusOutlined />} onClick={add}>افزودن تعطیلی</Button>
      </Space>
      <GxTable dataSource={rows} columns={columns} rowKey="id" size="small" pagination={{ pageSize: 15 }} />
    </Card>
  );
}

// ==================== تب مرخصی ====================
function LeavesTab() {
  const cj = currentJalali();
  const [rows, setRows] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [year, setYear] = useState(cj.year);
  const [month, setMonth] = useState(cj.month);
  const [empId, setEmpId] = useState(null);
  const [date, setDate] = useState(todayISO());
  const [kind, setKind] = useState("unpaid");
  const [note, setNote] = useState("");

  const load = () => getLeaves({ year, month }).then(r => setRows(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [year, month]);
  useEffect(() => {
    getEmployees({ limit: 1000 }).then(r => {
      const list = Array.isArray(r.data) ? r.data : (r.data.items || []);
      setEmployees(list);
    });
  }, []);

  const add = async () => {
    if (!empId) { message.warning("پرسنل را انتخاب کنید"); return; }
    try {
      await createLeave({ employee_id: empId, leave_date: date, kind, note: note || null });
      // قاعدهٔ مرخصیِ مجاورِ تعطیل
      if (kind === "unpaid") {
        const adj = await getAdjacentHolidays(date);
        if (adj.data.length > 0) {
          Modal.confirm({
            title: "تعطیلِ مجاور",
            content: `این مرخصی مجاورِ تعطیلِ باحقوق است: ${adj.data.map(h => `${h.jalali_date} (${h.position})`).join("، ")}. آیا این تعطیل(ها) هم برای این پرسنل مرخصیِ بی‌حقوق ثبت شود (کسر شود)؟`,
            okText: "بله، کسر شود", cancelText: "خیر",
            onOk: async () => {
              for (const h of adj.data) {
                await createLeave({ employee_id: empId, leave_date: h.holiday_date, kind: "unpaid", note: "مجاورِ مرخصی" });
              }
              load();
            },
          });
        }
      }
      message.success("مرخصی ثبت شد");
      setNote("");
      load();
    } catch { message.error("خطا"); }
  };

  const columns = [
    { title: "پرسنل", dataIndex: "employee_name" },
    { title: "تاریخ", dataIndex: "jalali_date", width: 130 },
    { title: "نوع", dataIndex: "kind", width: 130,
      render: v => v === "paid"
        ? <Tag color="green">باحقوق</Tag>
        : <Tag color="red">بی‌حقوق</Tag> },
    { title: "یادداشت", dataIndex: "note" },
    { title: "", width: 60,
      render: (_, r) => (
        <Popconfirm title="حذف شود؟" onConfirm={async () => { await deleteLeave(r.id); load(); }}
          okText="بله" cancelText="خیر">
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ) },
  ];

  return (
    <Card
      title={<Space>
        <Select value={year} onChange={setYear} style={{ width: 90 }}>
          {JALALI_YEARS.map(y => <Option key={y} value={y}>{y}</Option>)}
        </Select>
        <Select value={month} onChange={setMonth} style={{ width: 100 }}>
          {JALALI_MONTHS.map((m, i) => <Option key={i + 1} value={i + 1}>{m}</Option>)}
        </Select>
      </Space>}
    >
      <Space wrap style={{ marginBottom: 14 }}>
        <Select showSearch value={empId} onChange={setEmpId} style={{ width: 200 }}
          placeholder="انتخاب پرسنل..." optionFilterProp="children"
          filterOption={filterByChildren}>
          {employees.map(e => <Option key={e.id} value={e.id}>{e.full_name} ({e.employee_code})</Option>)}
        </Select>
        <JalaliDateInput value={date} onChange={setDate} />
        <Select value={kind} onChange={setKind} style={{ width: 130 }}>
          <Option value="unpaid">بی‌حقوق</Option>
          <Option value="paid">باحقوق</Option>
        </Select>
        <Input placeholder="یادداشت" value={note} onChange={e => setNote(e.target.value)} style={{ width: 160 }} />
        <Button type="primary" icon={<PlusOutlined />} onClick={add}>ثبت مرخصی</Button>
      </Space>
      <GxTable dataSource={rows} columns={columns} rowKey="id" size="small" pagination={{ pageSize: 15 }} />
    </Card>
  );
}

// ==================== تب کاربران ====================
function UsersTab() {
  const [rows, setRows] = useState([]);
  const [roles, setRoles] = useState([]);
  const [cat, setCat] = useState([]);          // کاتالوگِ گروه‌بندی‌شدهٔ مجوزها
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState({ open: false, user: null });
  const [pwdOpen, setPwdOpen] = useState(false);
  const [useRole, setUseRole] = useState(true); // true = پیرویِ از نقش؛ false = دسترسیِ سفارشی
  const [permSet, setPermSet] = useState(() => new Set());
  // دسترسی‌های انحصاری (مثلِ اموال) — جدا از نقش و از «سفارشی»؛ حتی مدیرِ اصلی بدونِ تیک ندارد
  const [exclSet, setExclSet] = useState(() => new Set());
  const [form] = Form.useForm();
  const [pwdForm] = Form.useForm();

  const load = () => getUsers().then(r => setRows(r.data));
  useEffect(() => {
    load();
    getRoles().then(r => setRoles(r.data)).catch(() => {});
    getPermissionCatalog().then(r => setCat(r.data)).catch(() => {});
  }, []);

  const roleLabel = (v) => roles.find(r => r.value === v)?.label || ROLE_LABELS[v] || v;
  const rolePerms = (v) => roles.find(r => r.value === v)?.permissions || [];
  const exclKeys = new Set(cat.filter(g => g.exclusive).flatMap(g => g.perms.map(([p]) => p)));
  const splitPerms = (list) => {
    const shared = new Set(); const excl = new Set();
    (list || []).forEach(p => (exclKeys.has(p) ? excl : shared).add(p));
    return [shared, excl];
  };

  const openNew = () => {
    form.resetFields();
    setUseRole(true); setPermSet(new Set(rolePerms("management_viewer"))); setExclSet(new Set());
    setModal({ open: true, user: null });
  };
  const openEdit = (u) => {
    form.setFieldsValue({
      username: u.username, full_name: u.full_name, role: u.role,
      is_active: u.is_active, password: "", admin_pin: "", clear_pin: false,
      session_hours: u.session_hours || 0, home_page: u.home_page || "",
    });
    // «سفارشی» یعنی مجوزِ غیرِانحصاری در لیستِ صریح هست؛ تیکِ اموال به‌تنهایی کاربر را از نقش جدا نمی‌کند
    const [customShared, excl] = splitPerms(u.permissions);
    const custom = customShared.size > 0;
    setUseRole(!custom);
    setPermSet(custom ? customShared : splitPerms(u.effective_permissions || rolePerms(u.role))[0]);
    setExclSet(excl);
    setModal({ open: true, user: u });
  };

  // وقتی نقش تغییر کرد و حالتِ «پیرویِ از نقش» فعال است → شبکه را با باندلِ نقش پر کن
  const onRoleChange = (r) => { if (useRole) setPermSet(new Set(rolePerms(r))); };
  const togglePerm = (p) => setPermSet(s => { const n = new Set(s); n.has(p) ? n.delete(p) : n.add(p); return n; });
  // دسترسی‌های همین فرم (نقش یا تیک‌ها + انحصاری‌ها) — برای فهرستِ «صفحهٔ اول»
  const formRole = Form.useWatch("role", form);
  const hasFormPerm = (p) => (useRole ? rolePerms(formRole).includes(p) : permSet.has(p)) || exclSet.has(p)
    || formRole === "super_admin" && !exclKeys.has(p);
  const toggleExcl = (p) => setExclSet(s => { const n = new Set(s); n.has(p) ? n.delete(p) : n.add(p); return n; });

  const save = async () => {
    const v = await form.validateFields();
    // [] ⇒ بازگشت به باندلِ نقش؛ انحصاری‌ها در هر دو حالت جدا فرستاده می‌شوند
    const permPayload = [...(useRole ? [] : Array.from(permSet).filter(p => !exclKeys.has(p))), ...exclSet];
    try {
      if (modal.user) {
        const patch = { full_name: v.full_name, role: v.role, is_active: v.is_active, permissions: permPayload,
          session_hours: v.session_hours || 0, home_page: v.home_page || "" };
        if (v.password) patch.password = v.password;
        if (v.clear_pin) patch.admin_pin = "";
        else if (v.admin_pin) patch.admin_pin = v.admin_pin;
        await updateUser(modal.user.id, patch);
        message.success("کاربر ویرایش شد");
      } else {
        await createUser({
          username: v.username, full_name: v.full_name, role: v.role,
          password: v.password, is_active: v.is_active, permissions: permPayload,
          home_page: v.home_page || "",
          ...(v.admin_pin ? { admin_pin: v.admin_pin } : {}),
        });
        message.success("کاربر ایجاد شد");
      }
      setModal({ open: false, user: null });
      load();
    } catch (e) { if (e?.errorFields) return; message.error(e?.response?.data?.detail || "خطا"); }
  };

  // شبکهٔ چک‌باکسِ دسترسی‌ها (گروه‌بندی‌شده)
  const permGrid = (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "4px 0 10px" }}>
        <Switch checked={!useRole} onChange={(c) => {
          setUseRole(!c);
          if (!c) { const r = form.getFieldValue("role"); setPermSet(new Set(rolePerms(r))); }
        }} />
        <span style={{ fontSize: 13 }}>دسترسیِ سفارشی (جدا از نقش)</span>
        {useRole && <Tag color="blue" style={{ fontSize: 11 }}>پیرویِ از نقش</Tag>}
      </div>
      <div style={{ maxHeight: 260, overflowY: "auto", border: "1px solid var(--border,#eee)", borderRadius: 8, padding: 8, opacity: useRole ? 0.55 : 1 }}>
        {cat.filter(g => !g.exclusive).map(g => (
          <div key={g.key} style={{ marginBottom: 8 }}>
            <div style={{ fontWeight: 700, fontSize: 12.5, color: "#1F4E79", marginBottom: 4 }}>{g.label}</div>
            <Space wrap size={[10, 4]}>
              {g.perms.map(([p, lbl]) => (
                <Checkbox key={p} disabled={useRole} checked={permSet.has(p)} onChange={() => togglePerm(p)}>
                  <span style={{ fontSize: 12.5 }}>{lbl}</span>
                </Checkbox>
              ))}
            </Space>
          </div>
        ))}
      </div>
      {cat.filter(g => g.exclusive).map(g => (
        <div key={g.key} style={{ marginTop: 10, border: "1px dashed #cf1322", borderRadius: 8, padding: 8 }}>
          <div style={{ fontWeight: 700, fontSize: 12.5, color: "#cf1322", marginBottom: 2 }}>🔒 {g.label}</div>
          <div style={{ fontSize: 11.5, opacity: 0.75, marginBottom: 6 }}>
            در هیچ نقشی (حتی مدیرِ اصلی) نیست؛ فقط با همین تیک باز می‌شود — مستقل از «پیرویِ از نقش».
          </div>
          <Space wrap size={[10, 4]}>
            {g.perms.map(([p, lbl]) => (
              <Checkbox key={p} checked={exclSet.has(p)} onChange={() => toggleExcl(p)}>
                <span style={{ fontSize: 12.5 }}>{lbl}</span>
              </Checkbox>
            ))}
          </Space>
        </div>
      ))}
    </div>
  );

  const changePwd = async () => {
    const v = await pwdForm.validateFields();
    try {
      await changeMyPassword({ old_password: v.old_password, new_password: v.new_password });
      message.success("رمزِ ورودِ شما تغییر کرد");
      setPwdOpen(false); pwdForm.resetFields();
    } catch (e) { if (e?.errorFields) return; message.error(e?.response?.data?.detail || "خطا"); }
  };

  const fmtDateTime = (v) => {
    if (!v) return "—";
    try {
      const t = new Date(v).toLocaleTimeString("fa-IR", { hour: "2-digit", minute: "2-digit" });
      return `${toJalali(v)} ${t}`;
    } catch { return toJalali(v); }
  };

  const q = search.trim().toLowerCase();
  const filtered = q ? rows.filter(u => `${u.username} ${u.full_name}`.toLowerCase().includes(q)) : rows;

  const columns = [
    { title: "نام کاربری", dataIndex: "username", width: 130 },
    { title: "نام", dataIndex: "full_name" },
    { title: "نقش", dataIndex: "role", width: 150, render: (v, u) => (
        <Space size={2} wrap>
          <Tooltip title={rolePerms(v).length ? `مجوزها: ${rolePerms(v).join("، ")}` : undefined}>
            <Tag>{roleLabel(v)}</Tag>
          </Tooltip>
          {(u.effective_permissions || []).includes("assets.view") && <Tag color="red">🔒 اموال</Tag>}
        </Space>
      ) },
    { title: "آخرین ورود", dataIndex: "last_login", width: 150, render: fmtDateTime },
    { title: "مدتِ نشست", dataIndex: "session_hours", width: 110,
      render: (v) => (v ? <Tag color="blue">{v >= 24 && v % 24 === 0 ? `${v / 24} روز` : `${v} ساعت`}</Tag>
        : <Typography.Text type="secondary">پیش‌فرض</Typography.Text>) },
    { title: "تاریخ ساخت", dataIndex: "created_at", width: 110, render: v => v ? toJalali(v) : "—" },
    { title: "کد مدیریتی", dataIndex: "has_pin", width: 100,
      render: v => v ? <Tag color="green">دارد</Tag> : <Tag>ندارد</Tag> },
    { title: "وضعیت", dataIndex: "is_active", width: 90,
      render: v => v ? <Tag color="blue">فعال</Tag> : <Tag color="red">غیرفعال</Tag> },
    { title: "عملیات", width: 120,
      render: (_, u) => (
        <Space size={4}>
          <Button size="small" onClick={() => openEdit(u)}>ویرایش</Button>
          <Popconfirm title="حذف شود؟" onConfirm={async () => { try { await deleteUser(u.id); message.success("حذف شد"); load(); } catch (e) { message.error(e?.response?.data?.detail || "خطا"); } }}
            okText="بله" cancelText="خیر">
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ) },
  ];

  return (
    <Card extra={
      <Space wrap>
        <Button onClick={() => { pwdForm.resetFields(); setPwdOpen(true); }}>تغییرِ رمزِ من</Button>
        <Button type="primary" icon={<UserAddOutlined />} onClick={openNew}>کاربر جدید</Button>
      </Space>}>
      <Alert type="info" showIcon style={{ marginBottom: 12 }}
        message="«رمزِ ورود» برای واردشدن به سامانه است و «کدِ مدیریتی» برای تأییدِ عملیاتِ حساس (ویرایش/حذفِ موارد قفل‌شده). نقشِ هر کاربر مجوزهای او را تعیین می‌کند." />
      <div style={{ marginBottom: 12, maxWidth: 280 }}>
        <Input.Search allowClear placeholder="جست‌وجوی کاربر…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>
      <GxTable dataSource={filtered} columns={columns} rowKey="id" size="small" pagination={false} layoutId="settings-users" />

      <Modal title={modal.user ? "ویرایش کاربر" : "کاربر جدید"} open={modal.open}
        onOk={save} onCancel={() => setModal({ open: false, user: null })}
        okText="ذخیره" cancelText="انصراف" destroyOnHidden>
        <Form form={form} layout="vertical" style={{ direction: "rtl" }}>
          <Form.Item name="username" label="نام کاربری" rules={[{ required: !modal.user, message: "نام کاربری لازم است" }]}>
            <Input disabled={!!modal.user} autoComplete="off" />
          </Form.Item>
          <Form.Item name="full_name" label="نام کامل" rules={[{ required: true, message: "نام لازم است" }]}>
            <Input />
          </Form.Item>
          <Form.Item name="role" label="نقش (قالبِ دسترسی)" initialValue="management_viewer" rules={[{ required: true }]}>
            <Select onChange={onRoleChange} placeholder="انتخابِ نقش"
              options={roles.map(r => ({ value: r.value, label: r.label }))} />
          </Form.Item>
          <Form.Item label="دسترسی‌ها">{permGrid}</Form.Item>
          <Form.Item name="password"
            label={modal.user ? "ریستِ رمزِ ورود (خالی = بدونِ تغییر)" : "رمزِ ورود"}
            rules={modal.user ? [{ min: 4, message: "حداقل ۴ نویسه" }]
              : [{ required: true, message: "رمزِ ورود لازم است" }, { min: 4, message: "حداقل ۴ نویسه" }]}>
            <Input.Password placeholder="رمزِ ورود" autoComplete="new-password" />
          </Form.Item>
          <Form.Item name="admin_pin" label={modal.user ? "کدِ مدیریتیِ جدید (خالی = بدونِ تغییر)" : "کدِ مدیریتی (اختیاری)"}>
            <Input.Password placeholder="کدِ مدیریتی" autoComplete="new-password" />
          </Form.Item>
          {modal.user && (
            <Form.Item name="clear_pin" valuePropName="checked" style={{ marginTop: -8 }}>
              <Checkbox>کدِ مدیریتیِ این کاربر پاک شود</Checkbox>
            </Form.Item>
          )}
          {/* v2.144.0 — «هر چند ساعت یک‌بار ورودِ اجباری» (کاربر: حسابدار ۱ از چند سیستم وارد می‌شود ⇒ ۱۲ ساعت) */}
          {modal.user && (
            <Form.Item name="session_hours" label="مدتِ نشست — بعد از این مدت باید دوباره وارد شود"
              extra="۰ = پیش‌فرضِ سیستم (۷ روز). مثلاً ۱۲ ⇒ هر ۱۲ ساعت ورودِ اجباری؛ از همین لحظه روی همهٔ دستگاه‌هایش اعمال می‌شود.">
              <Select style={{ width: 240 }} options={[
                { value: 0, label: "پیش‌فرض (۷ روز)" }, { value: 1, label: "۱ ساعت" }, { value: 2, label: "۲ ساعت" },
                { value: 4, label: "۴ ساعت" }, { value: 8, label: "۸ ساعت" }, { value: 12, label: "۱۲ ساعت" },
                { value: 24, label: "۱ روز" }, { value: 48, label: "۲ روز" }, { value: 72, label: "۳ روز" },
              ]} />
            </Form.Item>
          )}
          {/* v2.169.0 — صفحه‌ای که بعد از ورود باز می‌شود (فقط از صفحه‌هایی که همین دسترسی‌ها اجازه می‌دهند) */}
          <Form.Item name="home_page" label="صفحهٔ اول بعد از ورود" initialValue=""
            extra="«خودکار» = اولین صفحه‌ای که دسترسی دارد. اگر بعداً دسترسیِ آن صفحه برداشته شود، خودکار می‌شود.">
            <Select style={{ width: 300 }} options={[
              { value: "", label: `خودکار — ${HOME_PAGES.find((p) => p.path === homeFor(null, hasFormPerm))?.label || "راهنما"}` },
              ...pagesFor(hasFormPerm).map((p) => ({ value: p.path, label: p.label })),
            ]} />
          </Form.Item>
          <Form.Item name="is_active" label="فعال" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="تغییرِ رمزِ ورودِ من" open={pwdOpen} onOk={changePwd} onCancel={() => setPwdOpen(false)}
        okText="تغییرِ رمز" cancelText="انصراف" destroyOnHidden>
        <Form form={pwdForm} layout="vertical" style={{ direction: "rtl" }}>
          <Form.Item name="old_password" label="رمزِ فعلی" rules={[{ required: true, message: "رمزِ فعلی لازم است" }]}>
            <Input.Password autoComplete="current-password" />
          </Form.Item>
          <Form.Item name="new_password" label="رمزِ جدید" rules={[{ required: true, message: "رمزِ جدید لازم است" }, { min: 4, message: "حداقل ۴ نویسه" }]}>
            <Input.Password autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}

// ==================== تب تنظیمات حقوق ====================
function PayrollSettingsTab() {
  const [s, setS] = useState(null);

  const load = () => getPayrollSettings().then(r => setS(r.data));
  useEffect(load, []);

  const save = async () => {
    try {
      await updatePayrollSettings({
        paid_minutes_per_day: s.paid_minutes_per_day,
        insurance_rate: s.insurance_rate,
        shortage_divisor_hours: s.shortage_divisor_hours,
        overtime_enabled: s.overtime_enabled,
        overtime_rate: s.overtime_rate,
      });
      message.success("تنظیمات ذخیره شد");
    } catch { message.error("خطا"); }
  };

  if (!s) return <Card loading />;
  return (
    <Card style={{ maxWidth: 520 }}>
      <Alert type="info" showIcon style={{ marginBottom: 16 }}
        message="«دقایق پرداختیِ یک روز» مبنای حقوقیِ نرخ کسرکار/اضافه‌کاری است (۴۲۰ دقیقه = ۷ ساعت = یک روز حقوقی). این ساعتِ حضورِ واقعی کارخانه (۸–۱۸ = ۱۰ ساعت) نیست." />
      <Form layout="vertical" style={{ direction: "rtl" }}>
        <Form.Item label="دقایق پرداختیِ یک روز (مبنای حقوقی)"
          help={`معادل ${formatDurationFa(s.paid_minutes_per_day)}`}>
          <InputNumber style={{ width: "100%" }} min={1} value={s.paid_minutes_per_day}
            onChange={v => setS({ ...s, paid_minutes_per_day: v })} />
        </Form.Item>
        <Form.Item label="نرخ بیمه (مثلاً ۰.۰۷ = ۷٪)">
          <InputNumber style={{ width: "100%" }} min={0} max={1} step={0.01} value={s.insurance_rate}
            onChange={v => setS({ ...s, insurance_rate: v })} />
        </Form.Item>
        <Form.Item label="مبنای ساعتیِ کسرکار (نمایشی)">
          <InputNumber style={{ width: "100%" }} min={1} value={s.shortage_divisor_hours}
            onChange={v => setS({ ...s, shortage_divisor_hours: v })} />
        </Form.Item>
        <Form.Item label="اضافه‌کاری فعال است؟">
          <Switch checked={s.overtime_enabled} onChange={v => setS({ ...s, overtime_enabled: v })} />
        </Form.Item>
        {s.overtime_enabled && (
          <Form.Item label="نرخ هر ساعت اضافه‌کاری (ریال)">
            <InputNumber style={{ width: "100%" }} min={0} value={s.overtime_rate}
              onChange={v => setS({ ...s, overtime_rate: v })} />
          </Form.Item>
        )}
        <Button type="primary" onClick={save}>ذخیره تنظیمات</Button>
      </Form>
    </Card>
  );
}

// ==================== تب تنظیمات حسابداری (Phase 1) ====================
function AccountingSettingsTab() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { getAccountingSettings().then(r => setS(r.data)).catch(() => {}); }, []);
  if (!s) return <Card loading />;

  const set = (k, v) => setS({ ...s, [k]: v });
  const save = async () => {
    setSaving(true);
    try { await updateAccountingSettings(s); message.success("تنظیمات حسابداری ذخیره شد"); }
    catch { message.error("خطا در ذخیره"); }
    setSaving(false);
  };

  const Row = ({ label, hint, children }) => (
    <div style={{ marginBottom: 18 }}>
      <Text strong style={{ display: "block", marginBottom: 4 }}>{label}</Text>
      {hint && <Text type="secondary" style={{ display: "block", fontSize: 12, marginBottom: 6 }}>{hint}</Text>}
      {children}
    </div>
  );

  return (
    <Card style={{ maxWidth: 680 }}>
      <Alert type="warning" showIcon style={{ marginBottom: 18 }}
        message="این تنظیمات فقط ذخیره می‌شوند"
        description="قوانین تأییدشدهٔ حسابدار اینجا نگه‌داری می‌شوند ولی هنوز به موتور محاسبهٔ حقوق وصل نیستند (فاز بعد). تغییر این مقادیر، حقوقِ محاسبه‌شدهٔ فعلی را عوض نمی‌کند." />

      <Row label="مبنای روز حقوق (ماهانه)" hint="حقوق ثابت همیشه بر مبنای ۳۰ روز؛ ماه ۳۱روزه = ۳۰ + ۱ روز اضافه.">
        <InputNumber style={{ width: "100%" }} min={28} max={31} value={s.monthly_base_days}
          onChange={v => set("monthly_base_days", v)} />
      </Row>

      <Row label="آستانهٔ تلورانس کسری (دقیقه)" hint="کسری کمتر از این مقدار نادیده گرفته می‌شود.">
        <InputNumber style={{ width: "100%" }} min={0} value={s.shortage_tolerance_minutes}
          onChange={v => set("shortage_tolerance_minutes", v)} />
      </Row>

      <Row label="دامنهٔ تلورانس کسری">
        <Segmented value={s.shortage_tolerance_scope}
          onChange={v => set("shortage_tolerance_scope", v)}
          options={[{ value: "daily", label: "روزانه" }, { value: "monthly", label: "ماهانه" }]} />
      </Row>

      <Row label="پنجشنبه روز کامل" hint="پنجشنبه با ساعت کوتاه‌تر، یک روز کاریِ کامل حساب شود.">
        <Switch checked={s.thursday_counts_as_full_day} onChange={v => set("thursday_counts_as_full_day", v)} />
      </Row>

      <Row label="جریمهٔ غیبتِ مجاور تعطیل" hint="غیبتِ یک‌طرفِ تعطیل، خودِ تعطیل را هم کسر می‌کند.">
        <Switch checked={s.holiday_adjacent_absence_penalty} onChange={v => set("holiday_adjacent_absence_penalty", v)} />
      </Row>

      <Row label="اضافه‌کاری نیازمند تأیید مدیریت">
        <Switch checked={s.overtime_requires_approval} onChange={v => set("overtime_requires_approval", v)} />
      </Row>

      {/* ===== ساعت کاری واقعی کارخانه ===== */}
      <div style={{ borderTop: "1px solid var(--border, #eee)", margin: "18px 0 14px", paddingTop: 14 }}>
        <Text strong style={{ fontSize: 15 }}>🏭 ساعت کاری واقعی کارخانه</Text>
        <Text type="secondary" style={{ display: "block", fontSize: 12, marginTop: 2 }}>
          ساعت حضورِ واقعی پرسنل در کارخانه (نمایشی/مرجع).
        </Text>
      </div>
      <Space style={{ marginBottom: 18 }} wrap>
        <span>
          <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>شروع کار</Text>
          <TimePicker format="HH:mm" minuteStep={5}
            value={s.normal_work_start_time ? dayjs(s.normal_work_start_time, "HH:mm") : null}
            onChange={t => set("normal_work_start_time", t ? t.format("HH:mm") : "08:00")} />
        </span>
        <span>
          <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>پایان کار</Text>
          <TimePicker format="HH:mm" minuteStep={5}
            value={s.normal_work_end_time ? dayjs(s.normal_work_end_time, "HH:mm") : null}
            onChange={t => set("normal_work_end_time", t ? t.format("HH:mm") : "18:00")} />
        </span>
        <span>
          <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 4 }}>مدت واقعی (ساعت)</Text>
          <InputNumber min={1} max={24} step={0.5} value={s.normal_actual_work_hours}
            onChange={v => set("normal_actual_work_hours", v)} />
        </span>
      </Space>

      {/* ===== مبنای حقوقی محاسبه کسری/اضافه‌کاری ===== */}
      <div style={{ borderTop: "1px solid var(--border, #eee)", margin: "4px 0 14px", paddingTop: 14 }}>
        <Text strong style={{ fontSize: 15 }}>📐 مبنای حقوقیِ محاسبهٔ کسری/اضافه‌کاری</Text>
        <Text type="secondary" style={{ display: "block", fontSize: 12, marginTop: 2 }}>
          هر <b>{faNum(Number(s.overtime_base_hours_per_day))} ساعت = یک روز حقوقی</b>.
          این عدد فقط برای محاسبهٔ <u>مبلغ</u> کسری و اضافه‌کاری است، نه ساعتِ حضورِ واقعی کارخانه.
          {" "}(معادل {formatDurationFa(Number(s.overtime_base_hours_per_day) * 60)} = {Number(s.overtime_base_hours_per_day) * 60} دقیقه.)
        </Text>
      </div>
      <Row label="مبنای حقوقیِ هر روز (ساعت)">
        <InputNumber style={{ width: "100%" }} min={1} max={24} value={s.overtime_base_hours_per_day}
          onChange={v => set("overtime_base_hours_per_day", v)} />
      </Row>

      <Row label="منبع بیمه" hint="بیمه از فایل ادارهٔ دارایی تهران ایمپورت می‌شود (نه فرمول داخلی).">
        <Segmented value={s.insurance_source}
          onChange={v => set("insurance_source", v)}
          options={[{ value: "imported_file", label: "فایل ایمپورتی" }, { value: "formula", label: "فرمول" }]} />
      </Row>

      {/* «اعمال قوانین جدید از دوره» برداشته شد — از ۱۴۰۵/۰۶/۱۷ فقط یک موتورِ حقوق هست */}
      <Row label="آستانهٔ هشدار QC (٪)" hint="اختلاف بیش از این درصد = هشدار.">
        <InputNumber style={{ width: "100%" }} min={0} max={100} value={s.qc_warning_threshold_percent}
          onChange={v => set("qc_warning_threshold_percent", v)} />
      </Row>
      <Row label="آستانهٔ بحرانی QC (٪)" hint="اختلاف بیش از این درصد = جدی.">
        <InputNumber style={{ width: "100%" }} min={0} max={100} value={s.qc_serious_threshold_percent}
          onChange={v => set("qc_serious_threshold_percent", v)} />
      </Row>

      <Button type="primary" loading={saving} onClick={save}>ذخیره</Button>
    </Card>
  );
}

// ==================== تب گزارش فعالیت (لاگ ممیزی) ====================
const ACTION_LABELS = {
  create: "ایجاد", edit: "ویرایش", delete: "حذف", soft_delete: "حذف (نرم)",
  approve: "تأیید", approve_accountant: "تأییدِ حسابدار", approve_management: "تأییدِ مدیریت",
  cancel: "لغو", reopen: "بازگشایی", lock: "قفل", finalize: "نهایی‌سازی",
  review: "بازبینی", calculate: "محاسبه", login: "ورود", change_password: "تغییرِ رمز",
  repay: "بازپرداخت", carry_forward: "انتقال به دورهٔ بعد", closing_revert: "برگشتِ مرحله",
  qc: "کنترلِ کیفی", qc_op_save: "ثبتِ کنترلِ کیفی", manual_correct: "اصلاحِ دستی",
  map_device: "نگاشتِ دستگاه", import_approve: "تأییدِ ایمپورت",
};
const ENTITY_LABELS = {
  user: "کاربر", employee: "پرسنل", attendance: "حضوروغیاب", attendance_monthly: "حضورِ ماهانه",
  attendance_review: "بازبینیِ حضور", attendance_raw: "ثبتِ خامِ دستگاه",
  payroll_period: "دورهٔ حقوق", payroll_record: "فیشِ حقوق", advance: "مساعده", deduction: "کسورات",
  finance_txn: "تراکنشِ مالی", employee_debt: "بدهیِ پرسنل",
  production_order: "سری تولید", production_entry: "ثبتِ تولید",
  sc_event: "رویدادِ زنجیره", work_schedule: "ساعتِ کاری", accounting_settings: "تنظیماتِ حسابداری",
};
const actionLabel = (a) => ACTION_LABELS[a] || a;
const entityLabel = (e) => ENTITY_LABELS[e] || e;
const ACTION_COLOR = {
  delete: "red", soft_delete: "volcano", edit: "blue", create: "green", approve: "green",
  approve_accountant: "green", approve_management: "green", login: "default",
  calculate: "purple", finalize: "gold", lock: "gold", cancel: "red", change_password: "magenta",
};

function ActivityLogTab() {
  const [rows, setRows] = useState([]);
  const [opts, setOpts] = useState({ actors: [], actions: [], entity_types: [] });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const EMPTY = { actor: undefined, action: undefined, entity_type: undefined, date_from: undefined, date_to: undefined };
  const [f, setF] = useState(EMPTY);

  useEffect(() => {
    getAuditOptions().then(r => setOpts(r.data)).catch(() => {});
    getUsers().then(r => setUsers(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    setLoading(true);
    getAuditLogs({ ...f, limit: 800 })
      .then(r => setRows(r.data))
      .catch(() => message.error("خطا در بارگذاریِ لاگ"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [JSON.stringify(f)]);

  const nameOf = (actor) => users.find(u => u.username === actor)?.full_name || actor;
  const fmtDateTime = (v) => {
    if (!v) return "—";
    try { return `${toJalali(v)} ${new Date(v).toLocaleTimeString("fa-IR", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}`; }
    catch { return toJalali(v); }
  };

  const columns = [
    { title: "زمان", dataIndex: "created_at", width: 180, render: fmtDateTime },
    { title: "کاربر", dataIndex: "actor", width: 145, render: v => <Tag color="geekblue">{nameOf(v)}</Tag> },
    { title: "عملیات", dataIndex: "action", width: 130, render: v => <Tag color={ACTION_COLOR[v] || "default"}>{actionLabel(v)}</Tag> },
    { title: "موجودیت", dataIndex: "entity_type", width: 150, render: entityLabel },
    { title: "شناسه", dataIndex: "entity_id", width: 80, render: v => v ?? "—" },
    { title: "یادداشت", dataIndex: "note", render: v => v || "" },
  ];

  const renderDiff = (rec) => {
    const parse = (s) => { try { return s ? JSON.parse(s) : null; } catch { return s; } };
    const oldV = parse(rec.old_value), newV = parse(rec.new_value);
    if (!oldV && !newV) return <Text type="secondary">جزئیاتی ثبت نشده.</Text>;
    const fmt = (o) => (typeof o === "object" && o)
      ? Object.entries(o).map(([k, val]) => <div key={k}><b>{k}:</b> {String(val)}</div>)
      : <div>{String(o)}</div>;
    return (
      <div style={{ display: "flex", gap: 28, flexWrap: "wrap", fontSize: 13 }}>
        {oldV && <div><Text type="secondary">قبل</Text>{fmt(oldV)}</div>}
        {newV && <div><Text type="secondary">بعد / مقدار</Text>{fmt(newV)}</div>}
      </div>
    );
  };

  return (
    <Card>
      <Alert type="info" showIcon style={{ marginBottom: 12 }}
        message="گزارشِ «چه کسی، چه کاری، چه زمانی». همهٔ اقداماتِ مهمِ سامانه با نامِ کاربرِ انجام‌دهنده ثبت می‌شوند. روی هر ردیف بزنید تا جزئیاتِ تغییر باز شود." />
      <Space wrap style={{ marginBottom: 12 }}>
        <Select allowClear showSearch optionFilterProp="label" placeholder="کاربر" style={{ width: 160 }} value={f.actor}
          onChange={v => setF(s => ({ ...s, actor: v }))}
          options={opts.actors.map(a => ({ value: a, label: nameOf(a) }))} />
        <Select allowClear showSearch optionFilterProp="label" placeholder="عملیات" style={{ width: 150 }} value={f.action}
          onChange={v => setF(s => ({ ...s, action: v }))}
          options={opts.actions.map(a => ({ value: a, label: actionLabel(a) }))} />
        <Select allowClear showSearch optionFilterProp="label" placeholder="موجودیت" style={{ width: 160 }} value={f.entity_type}
          onChange={v => setF(s => ({ ...s, entity_type: v }))}
          options={opts.entity_types.map(e => ({ value: e, label: entityLabel(e) }))} />
        <span>از:</span>
        <JalaliDateInput value={f.date_from} onChange={v => setF(s => ({ ...s, date_from: v }))} />
        <span>تا:</span>
        <JalaliDateInput value={f.date_to} onChange={v => setF(s => ({ ...s, date_to: v }))} />
        <Button onClick={() => setF(EMPTY)}>پاک‌کردنِ فیلترها</Button>
      </Space>
      <GxTable dataSource={rows} columns={columns} rowKey="id" size="small" loading={loading}
        layoutId="settings-activitylog" scroll={{ x: "max-content" }}
        pagination={{ pageSize: 50, showSizeChanger: true }}
        expandable={{ expandedRowRender: renderDiff, rowExpandable: (r) => !!(r.old_value || r.new_value) }} />
    </Card>
  );
}

export default function SettingsPage() {
  const { user, can } = useAuth();
  const isAdmin = ["admin", "super_admin", "manager"].includes(user?.role);
  const isSuperAdmin = user?.role === "super_admin";
  const canBackup = (can && can("settings.backup")) || isAdmin;
  const items = [
    { key: "work", label: "ساعت کاری", children: <WorkScheduleTab /> },
    { key: "holidays", label: "تقویم تعطیلات", children: <HolidaysTab /> },
    { key: "leaves", label: "مرخصی", children: <LeavesTab /> },
    ...(isAdmin ? [{ key: "users", label: "کاربران", children: <UsersTab /> }] : []),
    { key: "production-options", label: "محصول / رنگ / سایز", children: <ProductionOptionsTab /> },
    { key: "payroll", label: "تنظیمات حقوق", children: <PayrollSettingsTab /> },
    { key: "accounting", label: "تنظیمات حسابداری", children: <AccountingSettingsTab /> },
    // «تنظیمات سیستم» — سه بخشِ کاملاً جدا؛ عمداً ادغام نمی‌شوند
    ...(isSuperAdmin ? [{ key: "update", label: "بروزرسانی نرم‌افزار", children: <UpdateTab /> }] : []),
    ...(canBackup ? [{ key: "backup", label: "بکاپ", children: <BackupTab /> }] : []),
    ...(canBackup ? [{ key: "restore", label: "بازیابی", children: <RestoreTab /> }] : []),
    ...(isAdmin ? [{ key: "activity", label: "گزارش فعالیت", children: <ActivityLogTab /> }] : []),
    { key: "appearance", label: "ظاهر", children: <AppearanceTab /> },
  ];
  return (
    <div>
      <Title level={5} style={{ marginBottom: 16 }}>تنظیمات</Title>
      <GxTabs urlKey="tab" defaultActiveKey="work" items={items} />
    </div>
  );
}
